// Stage-01 data access for the projects workspace — wired to the Postgres-backed
// /api/projects endpoint (folded into the ta-records multiplexer). See memory:
// rights-analysis-rebuild.
import apiConfig from '../../../config/api';

const BASE = apiConfig.apiBaseUrl || '';

// Send the session cookie on every request; the server resolves the user from it.
const FETCH_OPTS = { credentials: 'include' };

// fetch with a hard timeout so a stalled request (dropped socket, backend
// restart mid-flight) rejects instead of hanging the caller forever.
function fetchT(url, opts = {}, timeoutMs = 12000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const { signal: ext, ...rest } = opts;
  if (ext) {
    if (ext.aborted) ctrl.abort();
    else ext.addEventListener('abort', () => ctrl.abort(), { once: true });
  }
  return fetch(url, { ...rest, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// Project lifecycle statuses. `tone` maps to a pill style in the CSS; the brand
// accent (info) is Urbanix cerulean per the brief's "green → Urbanix blue".
export const PROJECT_STATUS = {
  draft: { label: 'טיוטה', tone: 'neutral' },
  collecting: { label: 'איסוף מידע', tone: 'info' },
  statutory_review: { label: 'מצב סטטוטורי בבדיקה', tone: 'warning' },
  rights_done: { label: 'ניתוח זכויות הושלם', tone: 'success' },
};

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

// Deterministic placeholder-thumbnail bucket (1..4) from the project id, so the
// gallery gradients are stable per project without a stored image.
export function thumbFor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = (h * 31 + String(id).charCodeAt(i)) >>> 0;
  return (h % 4) + 1;
}

// filter: 'all' | 'mine' | 'shared' — server scopes to the user's firm + shares.
export async function listProjects(filter = 'all') {
  const q = filter && filter !== 'all' ? `?filter=${encodeURIComponent(filter)}` : '';
  const res = await fetchT(`${BASE}/api/projects${q}`, { ...FETCH_OPTS, headers: authHeaders() });
  if (!res.ok) throw new Error(`listProjects failed: ${res.status}`);
  const data = await res.json();
  return (data.projects || []).map((p) => ({ ...p, thumb: thumbFor(p.id) }));
}

// In-flight de-duplication: several screens (form → identify → statutory) and
// React StrictMode's double-invoke all load the same project at once, firing
// redundant identical GETs that pile up against the browser's ~6-connection
// cap. Share one request per id while it's in flight.
const inflightGet = new Map();
export async function getProject(id) {
  if (inflightGet.has(id)) return inflightGet.get(id);
  const p = (async () => {
    const res = await fetchT(`${BASE}/api/projects?id=${encodeURIComponent(id)}`, { ...FETCH_OPTS, headers: authHeaders() });
    if (!res.ok) throw new Error(`getProject failed: ${res.status}`);
    return res.json();
  })().finally(() => inflightGet.delete(id));
  inflightGet.set(id, p);
  return p;
}

// Persist the Stage-02 identification snapshot (spec §8) for the next stage.
export async function saveIdentification(id, identification) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}&action=identification`, {
    ...FETCH_OPTS,
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ identification }),
  });
  if (!res.ok) throw new Error(`saveIdentification failed: ${res.status}`);
  return res.json();
}

// Persist the Stage-03 statutory-analysis snapshot (chunk 03 §13).
export async function saveStatutory(id, statutory) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}&action=statutory`, {
    ...FETCH_OPTS,
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ statutory }),
  });
  if (!res.ok) throw new Error(`saveStatutory failed: ${res.status}`);
  return res.json();
}

// Persist the Stage-04 existing-condition snapshot (chunk 04 §13) — the route
// runs with their observations, the consolidated selected values, and the
// schematic those values assemble into.
export async function saveExisting(id, existing) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}&action=existing`, {
    ...FETCH_OPTS,
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ existing }),
  });
  if (!res.ok) throw new Error(`saveExisting failed: ${res.status}`);
  return res.json();
}

export async function updateProject(id, payload) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}`, {
    ...FETCH_OPTS,
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `updateProject failed: ${res.status}`);
  }
  return res.json();
}

export async function createProject(payload) {
  const res = await fetch(`${BASE}/api/projects`, {
    ...FETCH_OPTS,
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `createProject failed: ${res.status}`);
  }
  return res.json();
}

export async function deleteProject(id) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}`, {
    ...FETCH_OPTS,
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`deleteProject failed: ${res.status}`);
  return res.json();
}

// --- Per-project sharing ----------------------------------------------------
export async function listShares(id) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}&action=shares`, {
    ...FETCH_OPTS, headers: authHeaders(),
  });
  if (!res.ok) throw new Error('טעינת השיתופים נכשלה.');
  return res.json(); // { visibility, shares: [{id, email, name, role, status}] }
}

export async function addShare(id, email, role) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}&action=share`, {
    ...FETCH_OPTS, method: 'POST', headers: authHeaders(), body: JSON.stringify({ email, role }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'השיתוף נכשל.');
  return data;
}

export async function removeShare(id, shareId) {
  const res = await fetch(`${BASE}/api/projects?id=${encodeURIComponent(id)}&action=share&shareId=${encodeURIComponent(shareId)}`, {
    ...FETCH_OPTS, method: 'DELETE', headers: authHeaders(),
  });
  if (!res.ok) throw new Error('הסרת השיתוף נכשלה.');
  return res.json();
}

export async function setProjectVisibility(id, visibility) {
  return updateProject(id, { visibility });
}

// Format an ISO date as DD.MM.YY to match the visual's "עודכן לאחרונה" line.
export function formatUpdated(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}.${mm}.${yy}`;
}
